import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import api from '../../api';
import { agencyToday, fmtTime } from '../../utils/timeclock';
import { StatusChip } from './VisitBits';

const Fit = ({ points }) => {
  const map = useMap();
  useEffect(() => { if (points.length) map.fitBounds(points, { padding: [40, 40], maxZoom: 15 }); }, [map, points]);
  return null;
};

// A day's visits: gold = patient's home, green/red = where the nurse clocked in (red = more than 0.25 mi away)
export const VisitMap = () => {
  const [day, setDay] = useState(agencyToday());
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/timeclock/visit-map', { params: { date: day } }).then((r) => setVisits(r.data.visits)).catch(() => toast.error('Could not load the map')).finally(() => setLoading(false));
  }, [day]);
  useEffect(() => { load(); }, [load]);
  const points = useMemo(() => visits.flatMap((v) => [v.home && [v.home.lat, v.home.lng], v.clockIn && [v.clockIn.lat, v.clockIn.lng]]).filter(Boolean), [visits]);
  const noHome = visits.filter((v) => !v.home);
  return (
    <div className="space-y-3" data-testid="visit-map">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="border rounded px-2 py-1.5" data-testid="visit-map-date" />
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#d4af37] border border-gray-700" /> Patient's home</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-600" /> Clock-in at home</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-600" /> Clock-in off-site</span>
        {loading && <span className="text-gray-500" data-testid="visit-map-loading">Finding addresses…</span>}
      </div>
      <div className="h-[460px] rounded-lg overflow-hidden border relative z-0" data-testid="visit-map-canvas">
        <MapContainer center={[33.8, -118.19]} zoom={11} style={{ height: '100%', width: '100%' }} scrollWheelZoom zoomControl={false}>
          <ZoomControl position="bottomright" />
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Fit points={points} />
          {visits.map((v) => {
            const off = v.entry?.inGeo?.offsite;
            return (
              <React.Fragment key={v.appointmentId}>
                {v.home && (
                  <CircleMarker center={[v.home.lat, v.home.lng]} radius={9} pathOptions={{ color: '#333', weight: 1.5, fillColor: '#d4af37', fillOpacity: 0.95 }}>
                    <Popup><strong>{v.patientName}</strong><br />{v.address}<br />{v.time} · {v.type}<br />{v.clinician}<br /><StatusChip status={v.status} /></Popup>
                  </CircleMarker>
                )}
                {v.clockIn && (
                  <CircleMarker center={[v.clockIn.lat, v.clockIn.lng]} radius={7} pathOptions={{ color: off ? '#b91c1c' : '#15803d', fillColor: off ? '#dc2626' : '#16a34a', fillOpacity: 0.9 }}>
                    <Popup><strong>{v.entry?.staffName || v.clinician}</strong> clocked in {fmtTime(v.entry?.clockIn)}<br />for {v.patientName}
                      {v.entry?.inGeo?.miles != null && <><br />{v.entry.inGeo.miles} mi from the home</>}</Popup>
                  </CircleMarker>
                )}
                {v.home && v.clockIn && <Polyline positions={[[v.home.lat, v.home.lng], [v.clockIn.lat, v.clockIn.lng]]} pathOptions={{ color: off ? '#dc2626' : '#16a34a', dashArray: '4 6', weight: 2, interactive: false }} />}
              </React.Fragment>
            );
          })}
        </MapContainer>
      </div>
      {!loading && !visits.length && <p className="text-sm text-gray-500" data-testid="visit-map-empty">No visits scheduled on this day.</p>}
      {noHome.length > 0 && <p className="text-xs text-amber-700" data-testid="visit-map-no-home">Address not found on the map for: {noHome.map((v) => v.patientName).join(', ')}. Check those addresses in the patient chart.</p>}
    </div>
  );
};
